/**
 * Pure helpers over the `/aps/rvt-models` graph. Kept out of components so
 * the graph math — indexing, link lookups, the group pre-fill and
 * completeness warning from the model-group design — is readable and
 * testable without rendering anything.
 *
 * The graph is consulted for exactly one thing in the app: the "links to a
 * model not in this group" tip. It never supplies folder structure or a
 * folder's contents — the folder browser reads ACC directly for those.
 */

import { RvtModelEdge, RvtModelNode } from '../../@types/projects';

export function indexNodesByUrn(nodes: RvtModelNode[]): Map<string, RvtModelNode> {
    return new Map(nodes.map(node => [node.lineage_urn, node]));
}

/** Build an undirected adjacency map once, so repeated neighbor lookups don't re-scan every edge. */
export function buildAdjacency(edges: RvtModelEdge[]): Map<string, Set<string>> {
    const adjacency = new Map<string, Set<string>>();
    const link = (a: string, b: string) => {
        let set = adjacency.get(a);
        if (!set) { set = new Set(); adjacency.set(a, set); }
        set.add(b);
    };
    for (const edge of edges) {
        link(edge.from, edge.to);
        link(edge.to, edge.from);
    }
    return adjacency;
}

/**
 * A model links others (`from === lineageUrn`) — the directed, host-to-link
 * relationship the backend reports. Used for the completeness warning,
 * which is specifically about a member being a link *host* to a model
 * outside the group (model-group-proposal.md §4.1, §8.4).
 */
export function outgoingLinks(lineageUrn: string, edges: RvtModelEdge[]): string[] {
    return edges.filter(e => e.from === lineageUrn).map(e => e.to);
}

/**
 * The connected component containing `seedUrns`, treating links as
 * undirected — the §4.1 pre-fill: "a new group starts as the selected
 * model's link closure." Direction matters for the completeness warning,
 * not for "what belongs together."
 */
export function connectedComponent(seedUrns: string[], adjacency: Map<string, Set<string>>): Set<string> {
    const visited = new Set<string>(seedUrns);
    const queue = [...seedUrns];
    while (queue.length > 0) {
        const current = queue.pop()!;
        for (const neighbor of adjacency.get(current) ?? []) {
            if (visited.has(neighbor)) continue;
            visited.add(neighbor);
            queue.push(neighbor);
        }
    }
    return visited;
}

export interface UnresolvedLink {
    from: string;
    to: string;
}

/**
 * Members that link a model outside the group — the completeness warning
 * from §4.1, computed client-side as the backend never states it (§8.4:
 * "it never blocks anything... the client holds both and needs nothing
 * from the backend to derive it"). Only edges whose target actually exists
 * in the catalog are reportable; an edge to nothing (unresolved by the
 * graph itself, §6.2) has nothing to offer an "Add it" action for.
 */
export function unresolvedLinks(
    memberUrns: ReadonlySet<string>,
    edges: RvtModelEdge[],
    nodesByUrn: Map<string, RvtModelNode>
): UnresolvedLink[] {
    const result: UnresolvedLink[] = [];
    for (const edge of edges) {
        if (!memberUrns.has(edge.from)) continue;
        if (memberUrns.has(edge.to)) continue;
        if (!nodesByUrn.has(edge.to)) continue;
        result.push({ from: edge.from, to: edge.to });
    }
    return result;
}

/** A model's display name, falling back to a short, still-unique tail of its lineage URN when the catalog has no name for it. */
export function displayName(node: RvtModelNode | undefined, lineageUrn: string): string {
    if (node?.name) return node.name;
    const tail = lineageUrn.split(':').pop() ?? lineageUrn;
    return tail.length > 12 ? `…${tail.slice(-12)}` : tail;
}
